import classNames from "classnames";
import { delay } from "lodash";
import { EnvelopeOpen } from "phosphor-react";
import React, { useEffect, useRef, useState } from "react";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import { ButtonClickPayloadGA } from "../../../../models/types/GAPayload.types";
import {
  authCheckVerificationAction,
  authLoginEmailResendAction,
  authLoginEmailResendResetAction,
  authLoginErrorRestAction,
  authLoginHalfDisabledUIAction,
  authVerifyEmailAction,
} from "../../../../redux/actions/auth.actions";
import {
  addEmailCheckVerificationAction,
  userAlternateLoginEmailResendAction,
  userAlternateLoginEmailResendResetAction,
  userErrorResetAction,
} from "../../../../redux/actions/user.actions";
import { AppState } from "../../../../redux/reducers";
import { AuthState } from "../../../../redux/reducers/auth.reducer";
import { UserState } from "../../../../redux/reducers/user.reducer";
import {
  authSelector,
  userSelector,
} from "../../../../redux/selectors/app.selectors";
import {
  EMAIL,
  EMAIL_CODE,
  INVITE_ID,
  REFERRAL_CODE,
  webStorageService,
} from "../../../../services/WebStorageService";
import Utils from "../../../../utils/Utils";
import { APIErrorCode, GAEventName } from "../../../../utils/enums";
import TopRoundedSpinner from "../../Spinner/TopRoundedSpinner";
import BrandButton from "../../custom-components/BrandButton";
import BrandHeading from "../../custom-components/heading/BrandHeading";
import CustomModal from "../CustomModal";

interface Props {
  showEmailVerificationModal: boolean;
  setShowEmailVerificationModal: Function;
  addAlternateFlow?: boolean;
  setEmail?: any;
  isEmailLoginOTPType: boolean;
}

const EmailVerificationModal: React.FC<Props> = ({
  setShowEmailVerificationModal,
  showEmailVerificationModal,
  addAlternateFlow,
  setEmail,
  isEmailLoginOTPType,
}) => {
  const dispatch = useDispatch();
  const authState: AuthState = useSelector(authSelector);
  const userState: UserState = useSelector(userSelector);
  const verificationInterval = useRef<any>();
  const inviteState = useSelector((state: AppState) => state.invite);

  const [emailResending, setEmailResending] = useState<any>(false);
  const [otp, setOtp] = useState<string>("");
  const [countDown, setCountDown] = useState<any>(30000);

  const [otpError, setOtpError] = useState(false);

  // 1: If Logging In
  const email = webStorageService.getValue(EMAIL);
  const emailCode = webStorageService.getValue(EMAIL_CODE);
  // 2: If adding alternate email login
  const alternateEmail = userState?.emailToVerify;
  const alternateEmailCode = userState?.email_identification_code;

  const error = addAlternateFlow ? userState.error : authState.error;
  const isEmailResent = addAlternateFlow
    ? userState.isEmailResent
    : authState.isEmailResent;

  const isInvalidCodeError = error?.includes("30");
  // Start initial timer of 30 secs.
  useEffect(() => {
    if (showEmailVerificationModal) {
      setCountDown(30000);
    }
  }, [showEmailVerificationModal]);

  // Whenever email is resent successfully, reset the 30 secs timer
  useEffect(() => {
    if (showEmailVerificationModal) {
      if (!!isEmailResent) {
        setEmailResending(false);
        setCountDown(30000);
      } else {
        setEmailResending(false);
      }
    }
  }, [isEmailResent, showEmailVerificationModal]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCountDown((prevCountDown: any) => {
        if (prevCountDown > 0) {
          return prevCountDown - 1000;
        }
      });
    }, 1000);
    if (countDown === 0 || (error && !isInvalidCodeError)) {
      clearTimeout(timeout);
      setCountDown(undefined);
      if (addAlternateFlow)
        dispatch(userAlternateLoginEmailResendResetAction());
      else dispatch(authLoginEmailResendResetAction());
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [
    error,
    countDown,
    dispatch,
    emailResending,
    addAlternateFlow,
    isInvalidCodeError,
  ]);

  // Polling
  useEffect(() => {
    // Polling in every 3 sec
    if (!isEmailLoginOTPType || addAlternateFlow) {
      if (showEmailVerificationModal) {
        verificationInterval.current = setInterval(() => {
          // If logging in
          const emailCode = webStorageService.getValue(EMAIL_CODE);
          const referral_code = webStorageService.getValue(REFERRAL_CODE);
          const invite_id = webStorageService.getValue(INVITE_ID);
          if (addAlternateFlow) {
            if (
              alternateEmail &&
              alternateEmailCode &&
              !userState?.userData?.email
            ) {
              dispatch(
                addEmailCheckVerificationAction({
                  req: {
                    code: alternateEmailCode!,
                  },
                })
              );
            }
          } else if (
            emailCode &&
            authState.userID === -1 &&
            !authState.loading
          ) {
            dispatch(
              authCheckVerificationAction({
                code: emailCode,
                referral_code,
                invite_id,
              })
            );
          }
        }, 3000);
      }
    }
    return () => {
      if (
        (!isEmailLoginOTPType || addAlternateFlow) &&
        verificationInterval?.current
      ) {
        clearInterval(verificationInterval.current);
      }
    };
  }, [
    dispatch,
    showEmailVerificationModal,
    authState.userID,
    userState?.userData?.email,
    alternateEmailCode,
    alternateEmail,
    userState.loading,
    authState.loading,
    addAlternateFlow,
    isEmailLoginOTPType,
  ]);

  useEffect(() => {
    // To open the Email verification modal when there is error eg. Code expired etc..
    if (!!error && !isInvalidCodeError) {
      clearInterval(verificationInterval.current);
      delay(() => {
        setShowEmailVerificationModal(true);
      }, 300);
    }
    if (error && isInvalidCodeError && isEmailLoginOTPType) {
      setOtp("");
      setOtpError(true);
      dispatch(authLoginErrorRestAction());
    }
  }, [setShowEmailVerificationModal, error, isInvalidCodeError, dispatch, isEmailLoginOTPType]);

  const changeEmailHandler = () => {
    setShowEmailVerificationModal(false);
    delay(async () => {
      setCountDown(undefined);
      setEmailResending(false);
      await webStorageService.removeValue(EMAIL_CODE);
      await webStorageService.removeValue(EMAIL);
    }, 200);

    if (addAlternateFlow) {
      setEmail(alternateEmail);
      dispatch(userAlternateLoginEmailResendResetAction());
    } else {
      dispatch(authLoginEmailResendResetAction());
    }
    Utils.GAEvent<ButtonClickPayloadGA>(GAEventName.BUTTON_CLICK, {
      event_category: "Button",
      event_label: "Change Login Email",
    });
    dispatch(authLoginHalfDisabledUIAction({
      req: {
        is_disabled: false,
      },
    }))

  };


  return (
    <CustomModal
      modalClassName="w-[99%] sm:w-[90%] md:w-[65%] max-w-[550px]"
      onClose={changeEmailHandler}
      open={showEmailVerificationModal}
      showCross
      closeOnOutSideClick={false}
    >
      <div className="flex flex-col items-center w-full p-6 py-12 text-center xs:py-12 xs:px-6">
        {!error || (isInvalidCodeError && isEmailLoginOTPType) ? (
          <div className="flex flex-col items-center w-full min-h-full">
            <EnvelopeOpen className="w-16 h-16 md:w-20 md:h-20" />
            <h1 className="text-2xl font-bold xs:text-3xl">
              Check your email!
            </h1>
            <div className="flex flex-col items-center px-3 text-base xs:text-lg">
              <span className="font-normal">
                We emailed you{" "}
                {isEmailLoginOTPType && !addAlternateFlow ? "a one-time PIN" : "a link"}{" "}
                to verify.
              </span>
              {!addAlternateFlow && (
                <span className="font-bold text-primaryText mt-2">
                  Do not close this tab.
                </span>
              )}
              <span className="mt-4 text-lg font-bold break-all text-brandGreen md:text-xl">
                {!addAlternateFlow ? (
                  email ? (
                    email
                  ) : (
                    <TopRoundedSpinner size="xs" />
                  )
                ) : alternateEmail ? (
                  alternateEmail
                ) : (
                  <TopRoundedSpinner size="xs" />
                )}
              </span>
              {!inviteState.invite?.email && (
                <>
                  <span className="font-normal mt-2">Is this correct? </span>
                  <button
                    onClick={changeEmailHandler}
                    className="text-base underline font-medium"
                  >
                    CLICK HERE TO CHANGE EMAIL
                  </button>
                </>
              )}
            </div>
            {isEmailLoginOTPType && !addAlternateFlow && (
              <div className="pt-8 ">
                <OtpInput
                  shouldAutoFocus
                  value={otp}
                  onChange={(str) => {
                    setOtp(str);
                    if (str.length === 6) {
                      const referral_code = webStorageService.getValue(REFERRAL_CODE);
                      const invite_id = webStorageService.getValue(INVITE_ID);
                      setOtpError(false);
                      dispatch(
                        authCheckVerificationAction({
                          code: emailCode,
                          otp: str,
                          referral_code,
                          invite_id
                        })
                      );
                    }
                  }}
                  numInputs={6}
                  inputType="number"
                  renderInput={(props) => (
                    <input
                      {...(props as any)}
                      disabled={otp.length === 6}
                      className={classNames(
                        "mx-1 sm:mx-2 !h-9 !w-9 text-sm sm:text-base sm:!h-14 sm:!w-14 rounded-md block bg-primaryText/[2.5%] transition-all",
                        `ring-1 hover:ring-2 ${otpError
                          ? "ring-brandError/50 hover:ring-brandError focus:ring-brandError"
                          : "ring-primaryText/50 hover:ring-primaryText focus:ring-primaryText"
                        } disabled:opacity-50 disabled:pointer-events-none placeholder-brandGray/75`,
                        "border-0 duration-300 focus:ring-1 focus:outline-none peer"
                      )}
                    />
                  )}
                />
              </div>
            )}

            <div className="w-full mt-8 px-5 max-w-[400px]">
              <BrandButton
                isSubmitting={emailResending}
                className="w-full h-12 text-base font-semibold"
                disabled={emailResending === true || !!countDown}
                onClick={() => {
                  setEmailResending(true);
                  {
                    !addAlternateFlow
                      ? dispatch(
                        authLoginEmailResendAction({
                          code: emailCode,
                        })
                      )
                      : dispatch(
                        userAlternateLoginEmailResendAction({
                          code: alternateEmailCode!,
                          email: alternateEmail!,
                        })
                      );
                  }
                  Utils.GAEvent<ButtonClickPayloadGA>(
                    GAEventName.BUTTON_CLICK,
                    {
                      event_category: "Button",
                      event_label: "Resend Email",
                    }
                  );
                }}
              >
                {!emailResending ? (
                  <>
                    {countDown ? (
                      <>
                        <span className="hidden xs:flex">
                          Resend After {countDown / 1000} sec(s)
                        </span>
                        <span className="flex xs:hidden">
                          Resend ({countDown / 1000} s)
                        </span>
                      </>
                    ) : (
                      <span className="font-semibold">
                        Resend Email
                      </span>
                    )}
                  </>
                ) : (
                  <>Resending</>
                )}
              </BrandButton>
            </div>
            <button
              className="mt-5 text-base underline font-medium"
              onClick={changeEmailHandler}
            >
              {addAlternateFlow ? "I WILL DO IT LATER" : "CANCEL"}
            </button>
          </div>
        ) : (
          <div className="space-y-7">
            <div className="flex flex-col items-center">
              <BrandHeading className="text-base xs:text-lg text-center !text-brandError !font-secondary">
                {error?.includes(APIErrorCode.VERIFICATION_CODE_EXPIRED)
                  ? addAlternateFlow
                    ? "Verification Code Expired. Please Verify Again."
                    : "Verification Code Expired. Please Log In Again."
                  : error?.includes(APIErrorCode.EMAIL_IS_NOT_WHITELISTED)
                    ? "This email / wallet address is not white-listed."
                    : error?.includes("501")
                      ? "Too soon! Try again after 30 secs!"
                      : "Something went wrong"}
              </BrandHeading>
            </div>
            <BrandButton
              className="h-12 text-base font-semibold"
              onClick={async () => {
                setShowEmailVerificationModal(false);
                await webStorageService.removeValue(EMAIL_CODE);
                await webStorageService.removeValue(EMAIL);

                delay(() => {
                  addAlternateFlow
                    ? dispatch(userErrorResetAction())
                    : dispatch(authLoginErrorRestAction());
                }, 200);
              }}
            >
              Go Back
            </BrandButton>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default React.memo(EmailVerificationModal);
